import * as React from "react";
import "./styles.scss";
import {VeBoxModal} from "../../../../../components/modal/VeBoxModal";
import {VeBoxCard} from "../../../../../components/card/VeBoxCard";
import {FormCardFooter} from "../../../../common";
import {DropzoneOptions, FileRejection, FileWithPath} from "react-dropzone";
import {Display} from "../../../../../components/Display";
import {RestApiError} from "../../data/models/RestApi";
import {DefaultErrorBoundaryFallback} from "../../../../../ErrorBoundary/DefaultErrorBoundaryFallback";
import {CensusCoolerContext} from "../../providers/CensusCollerProvider";
import {DropzoneArea} from "../../components/DropzoneArea";
import {ImportEquipmentContext} from "../../providers/ImportEquipmentProvider";
import {capitalizeFirstLetter} from "@vebox/etui-core";

type importEquipmentModalProps = {
    closeDialog: (doNothing: boolean) => void;
};

export const ImportEquipmentModal: React.FC<importEquipmentModalProps> = ({
                                                                              closeDialog,
                                                                          }) => {
    const {addEntriesByFile} = React.useContext(ImportEquipmentContext);

    const {AppTranslations} = React.useContext(CensusCoolerContext);

    const [addingComponents, setAddingComponents] =
        React.useState<boolean>(false);
    const [importedFile, setImportedFile] = React.useState<FileWithPath>();
    const [error, setError] = React.useState<RestApiError>();
    const [fileUploadError, setFileUploadError] = React.useState<string>();

    const option: DropzoneOptions = React.useMemo(() => {
        return {
            accept: {
                "text/csv": [".csv"],
                "application/vnd.ms-excel": [".xls"],
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
                    ".xlsx",
                ],
            },
            maxSize: 650000,//650kb
            maxFiles: 1,
            onDropRejected: (fileRejections) => manageFilesErrors(fileRejections),
            onError: (error) => {
                console.error(error);
            },
            disabled: addingComponents,
        }
    }, [addingComponents])

    const manageFilesErrors = (fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            const errors = fileRejections[0]?.errors.map((fileRejection) => {
                switch (fileRejection.code) {
                    case "file-invalid-type":
                        return AppTranslations.errorMessages.invalidFileType;
                    case "file-too-large":
                        return AppTranslations.errorMessages.fileTooLarge;
                    default:
                        return fileRejection.message;
                }
            });
            if (errors.length > 0) {
                setFileUploadError(errors.join(", "));
            }
        }
    }

    const addEntriesToCensusByFile = () => {
        setAddingComponents(true);
        addEntriesByFile(importedFile)
            .then((result) => {
                setAddingComponents(false);
                closeDialog(false);
            })
            .catch((error: RestApiError) => {
                setError(error);
                setAddingComponents(false);
            });
    };
    const onImportedFile = (file: FileWithPath[] | FileWithPath | undefined) => {
        Array.isArray(file) ? setImportedFile(file[0]) : setImportedFile(file);
        if ((Array.isArray(file) && file.length > 0)) {
            setFileUploadError(undefined);
        }
    };

    return (
        <VeBoxModal.Backdrop>
            <VeBoxModal>
                <VeBoxModal.Section style={{padding: 0}}>
                    <VeBoxCard className={"import-equipment-card"}>
                        <Display isVisible={!error}>
                            <div className={"dropzone-container"}>
                                <div className={"info-description space-between flx-row"}>
                                    <div>
                                        <p className={"csv-only"}>
                                            {
                                                AppTranslations.massiveActions.censusCooler
                                                    .equipmentCodeLimitation
                                            }
                                        </p>
                                        <p className={"csv-only"}>
                                            {
                                                AppTranslations.massiveActions.censusCooler
                                                    .csvFileLimitation
                                            }
                                        </p>
                                    </div>
                                </div>
                                <Display isVisible={fileUploadError}>
                                    <p className={"error-message space-between flx-row"}>
                                        {fileUploadError}
                                    </p>
                                </Display>
                                <div className={"dropzone-area"}>
                                    <DropzoneArea
                                        options={option}
                                        onImportedFile={onImportedFile}
                                    />
                                </div>
                            </div>
                        </Display>
                        <Display isVisible={error}>
                            <DefaultErrorBoundaryFallback
                                errorMessage={
                                    error?.message ??
                                    AppTranslations.errorMessages.somethingWentWrong
                                }
                                style={{backgroundColor: "white"}}
                            />
                        </Display>
                        <div className={"equipment-list-footer"}>
                            <FormCardFooter
                                onReset={() => closeDialog(true)}
                                onNext={addEntriesToCensusByFile}
                                restButtonText={capitalizeFirstLetter(
                                    AppTranslations.genericWords.cancel,
                                )}
                                nextButtonText={capitalizeFirstLetter(
                                    AppTranslations.genericWords.importTitle,
                                )}
                                loadable={true}
                                disabled={importedFile === undefined}
                                disabledCancel={addingComponents}
                                loading={addingComponents}
                            />
                        </div>
                    </VeBoxCard>
                </VeBoxModal.Section>
            </VeBoxModal>
        </VeBoxModal.Backdrop>
    );
};
